import type { TicketSummary } from "@/client/features/tickets/tickets-api";
import { apiFetch } from "@/client/lib/api";
import type { PropagationStatus } from "@/shared/propagation";

/**
 * Typed client for the tenant source-IP allowlist endpoints. Reads are open to
 * any member; writes are tenant-admin only (enforced server-side). All requests
 * are tenant-scoped server-side (RLS), so the client never passes a tenant id.
 */

export interface AllowedIp {
  id: string;
  value: string;
  label: string | null;
  createdByName: string | null;
  /** Propagation state into customer firewall rules (PE-924); "Propagated" once verified. */
  status: PropagationStatus;
  verifiedAt: string | null;
  createdAt: string;
}

export interface CreateAllowedIpInput {
  value: string;
  label?: string;
}

/** POST response: the created entry plus any non-blocking validation warnings. */
export type CreatedAllowedIp = AllowedIp & { warnings: string[] };

/** Ticket-based network-access request (PE-957): the ranges to allowlist + a note. */
export interface NetworkAccessRequestInput {
  ranges: string[];
  note?: string;
}

export const allowedIpsApi = {
  // Ticket-based request (PE-957): file a support ticket for the desired ranges.
  request: (input: NetworkAccessRequestInput) =>
    apiFetch<TicketSummary>("/allowed-ips/request", { method: "POST", json: input }),
  // The methods below drive the automated (Terraform-propagated) allowlist path.
  // Dormant while network access is ticket-based; retained for Stage 2 (PE-958).
  list: () => apiFetch<{ allowedIps: AllowedIp[] }>("/allowed-ips"),
  create: (input: CreateAllowedIpInput) =>
    apiFetch<CreatedAllowedIp>("/allowed-ips", { method: "POST", json: input }),
  remove: (id: string) => apiFetch<void>(`/allowed-ips/${id}`, { method: "DELETE" }),
  // Staff-only (during "view as"): flip an entry's propagation status.
  setPropagation: (id: string, status: PropagationStatus) =>
    apiFetch<AllowedIp>(`/allowed-ips/${id}/propagation`, { method: "PATCH", json: { status } }),
};
